import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ArrowLeftOutline, Button, Display, HStack, Stack, useToast } from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';
import { useAnalytics } from '@bonusly/who-am-i';

import { PageTitle } from '../../components/page/page-title';
import { FORM_PANEL_WIDTH } from '../../config/admin';
import { CelebrationType } from '../../entities/celebrations';
import { useCelebrationTypeMetadata, useCreateCelebration } from '../../queries/celebrations';
import { CelebrationForm } from './celebration-form';
import { useDefaultFormValue } from './hooks';
import { CelebrationFormData } from './types';
import { buildCreateFormBody, createSchema } from './utils';

export const CreateCelebration: FC = () => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'create' });
	const toast = useToast();
	const navigate = useNavigate();
	const { type } = useParams<{ type: CelebrationType }>();
	const { track } = useAnalytics();

	// SHARK-2331 - Fix this so default type is used if no type is provided, or an invalid one is passed
	assertPresence(type);

	const { data: metadata } = useCelebrationTypeMetadata(type);

	assertPresence(metadata);

	const { disabledValues, forceScope } = useMemo(() => {
		if (!metadata.restrictedConditions.length) {
			return { disabledValues: [], forceScope: undefined };
		}

		const existingScope = metadata.restrictedConditions[0];
		const forceScope = existingScope.condition.attr;
		const disabledValues = metadata.restrictedConditions.map((c) => c.condition.value);

		return { disabledValues, forceScope };
	}, []);

	const form = useForm<CelebrationFormData>({
		defaultValues: useDefaultFormValue(type),
		resolver: zodResolver(createSchema({ forceScope })),
	});

	const { mutate, isLoading } = useCreateCelebration({
		onMutate: () => {
			track('Create Celebration', { type });
		},
		onSuccess: () => {
			toast({
				title: t('createSuccess'),
				status: 'success',
			});
			navigate('/admin/programs/celebrations');
		},
		onError: () => {
			track('Create Celebration Failure', { type });

			toast({
				title: t('createFailure'),
				status: 'error',
			});
		},
	});

	return (
		<>
			<PageTitle>{t('title')}</PageTitle>

			<Stack px={10} py={8} spacing={6} maxW={FORM_PANEL_WIDTH}>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/celebrations" type="button">
						<ArrowLeftOutline size="xs" />
						{t('backButton')}
					</Button>
				</HStack>

				<Display size="sm">{t('display')}</Display>

				<CelebrationForm
					disabledValues={disabledValues}
					forceScope={forceScope}
					form={form}
					isLoading={isLoading}
					onSubmit={(data) => mutate(buildCreateFormBody(data))}
				/>
			</Stack>
		</>
	);
};
